/**
 * SEO Utility Functions
 * Helper functions for generating structured data, canonical URLs, and meta tags
 */

const SITE_URL = 'https://apexgateglobal.com';
const SITE_NAME = 'APEXGATE GLOBAL';

/** Default Organization schema fields (homepage / JSON-LD) */
const ORGANIZATION_DESCRIPTION =
  'Apexgate Global provides structured execution infrastructure for cross-border industrial engagements, supporting EPC contractors, utilities, advanced manufacturers, and institutional buyers across infrastructure, energy, strategic materials, aerospace, and healthcare sectors.';
const ORGANIZATION_AREA_SERVED = 'Global';
const ORGANIZATION_INDUSTRY = [
  'Infrastructure',
  'Energy',
  'Strategic Materials',
  'Aerospace',
  'Healthcare',
  'Industrial Manufacturing'
];
const ORGANIZATION_KNOWS_ABOUT = [
  'Cross-border industrial execution',
  'Manufacturer-aligned supply channels',
  'Strategic materials sourcing',
  'Infrastructure project supply chains',
  'Aerospace manufacturing supply chains',
  'Institutional procurement environments'
];

/**
 * Generate canonical URL for a given path
 * @param {string} path - The path (e.g., '/about', '/contact')
 * @returns {string} Full canonical URL
 */
export const getCanonicalUrl = (path = '') => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath === '/' ? '' : cleanPath}`;
};

/**
 * Generate Organization structured data
 * @param {Object} companyInfo - Company information from content
 * @returns {Object} Organization schema object
 */
export const generateOrganizationSchema = (companyInfo) => {
  const { footerInfo, contactInfo } = companyInfo;

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: footerInfo?.companyLegalName || footerInfo?.companyName || SITE_NAME,
    legalName: footerInfo?.companyLegalName || footerInfo?.companyName || SITE_NAME,
    url: SITE_URL,
    description: companyInfo?.description ?? ORGANIZATION_DESCRIPTION,
    areaServed: companyInfo?.areaServed ?? ORGANIZATION_AREA_SERVED,
    industry: companyInfo?.industry ?? ORGANIZATION_INDUSTRY,
    knowsAbout: companyInfo?.knowsAbout ?? ORGANIZATION_KNOWS_ABOUT,
    logo: `${SITE_URL}/logo/logo.png`,
    ...(footerInfo?.address && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: footerInfo.address,
        addressLocality: 'Wan Chai',
        addressRegion: 'Hong Kong',
        addressCountry: 'HK'
      }
    }),
    ...(contactInfo?.email && {
      email: contactInfo.email,
      contactPoint: {
        '@type': 'ContactPoint',
        email: contactInfo.email,
        contactType: 'Customer Service',
        ...(contactInfo?.phones?.sea && {
          telephone: contactInfo.phones.sea.replace(/[^+\d]/g, '')
        })
      }
    }),
    ...(footerInfo?.brn && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'BRN',
        value: footerInfo.brn
      }
    }),
    sameAs: [
      ...(contactInfo?.website ? [`https://${contactInfo.website}`] : [])
    ]
  };
};

/**
 * Generate WebSite structured data
 * @param {Object} siteInfo - Site information
 * @returns {Object} WebSite schema object
 */
export const generateWebSiteSchema = (siteInfo) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo?.name || SITE_NAME,
    url: SITE_URL,
    ...(siteInfo?.tagline && {
      description: siteInfo.tagline
    }),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate BreadcrumbList structured data
 * @param {Array} breadcrumbs - Array of {name, url} objects
 * @returns {Object} BreadcrumbList schema object
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url || getCanonicalUrl(crumb.path)
    }))
  };
};

/**
 * Generate WebPage structured data
 * @param {Object} pageInfo - Page information
 * @returns {Object} WebPage schema object
 */
export const generateWebPageSchema = (pageInfo) => {
  const { title, description, url, image, datePublished, dateModified } = pageInfo;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    ...(description && { description }),
    url: url || getCanonicalUrl(),
    ...(image && {
      image: image.startsWith('http') ? image : `${SITE_URL}${image}`
    }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL
    },
    about: {
      '@type': 'Organization',
      name: SITE_NAME
    }
  };
};

/**
 * Generate Service structured data
 * @param {Object} serviceInfo - Service information
 * @returns {Object} Service schema object
 */
export const generateServiceSchema = (serviceInfo) => {
  const { name, description, provider, areaServed, serviceType } = serviceInfo;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    ...(description && { description }),
    provider: {
      '@type': 'Organization',
      name: provider?.name || SITE_NAME,
      ...(provider?.url && { url: provider.url })
    },
    ...(areaServed && {
      areaServed: {
        '@type': 'Country',
        name: areaServed
      }
    }),
    ...(serviceType && { serviceType })
  };
};

/**
 * Generate Article structured data (for blog/content pages)
 * @param {Object} articleInfo - Article information
 * @returns {Object} Article schema object
 */
export const generateArticleSchema = (articleInfo) => {
  const { title, description, image, datePublished, dateModified, author } = articleInfo;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    ...(description && { description }),
    ...(image && {
      image: image.startsWith('http') ? image : `${SITE_URL}${image}`
    }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    author: {
      '@type': 'Organization',
      name: author || SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${SITE_URL}/logo/logo.png`
      }
    }
  };
};

/**
 * Generate default meta tags object
 * @param {Object} meta - Meta information
 * @returns {Object} Meta tags object
 */
export const generateMetaTags = (meta = {}) => {
  const {
    title,
    description,
    keywords,
    image,
    url,
    type = 'website',
    noindex = false
  } = meta;

  const canonicalUrl = url || getCanonicalUrl();
  const ogImage = image
    ? (image.startsWith('http') ? image : `${SITE_URL}${image}`)
    : `${SITE_URL}/logo/favicon_io/android-chrome-512x512.png`;

  return {
    title: title || `${SITE_NAME} – Strategic Infrastructure for International Growth`,
    description: description || 'Global leader in construction equipment, industrial products, electrical solutions, and comprehensive business consulting services.',
    keywords: keywords || 'construction equipment, industrial products, electrical products, chemical products, business consulting, international trade, procurement management, APEXGATE GLOBAL',
    canonical: canonicalUrl,
    'og:title': title || `${SITE_NAME} – Strategic Infrastructure for International Growth`,
    'og:description': description || 'Global leader in construction equipment, industrial products, and comprehensive business solutions',
    'og:type': type,
    'og:url': canonicalUrl,
    'og:site_name': SITE_NAME,
    'og:locale': 'en_US',
    'og:image': ogImage,
    'twitter:card': 'summary_large_image',
    'twitter:title': title || `${SITE_NAME} – Strategic Infrastructure for International Growth`,
    'twitter:description': description || 'Global leader in construction equipment, industrial products, and comprehensive business solutions',
    'twitter:image': ogImage,
    robots: noindex ? 'noindex, nofollow' : 'index, follow'
  };
};

export { SITE_URL, SITE_NAME };
